import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import { D3TooltipBuff, D3TooltipFollowerSkill, D3TooltipGem, D3TooltipItem, D3TooltipSkill } from 'tooltips';
import { ItemIcon, SkillHeader, SkillActive, SkillParams, SkillBoxes, SkillIcon, PassiveIcon } from './skillInfo';
import { getIconPath } from 'common';
import Select from 'ui/select';

const _L = Locale("ui-skills.js");

function BuffRuneSelect({ info, value, onChange }) {
  const renderRune = React.useCallback(rune => (
    <span className="d3p-rune-option d3p-skill-rune">
      <span className={`d3p-rune d3p-rune-${rune}`}/> {info.runes[rune]}
    </span>
  ), [info]);
  const renderRuneOption = React.useCallback(rune => (
    <D3TooltipSkill charClass={info.charClass} skill={info.id} rune={rune} runeOnly>
      {renderRune(rune)}
    </D3TooltipSkill>
  ), [info, renderRune]);

  return (
    <Select options={Object.keys(info.runes)}
            value={value}
            onChange={onChange}
            renderValue={renderRune}
            renderOption={renderRuneOption}
            className="d3p-select-rune ui-Select-noframe"
    />
  );
}

function BuffHeaderSkill({ info }) {
  const dispatch = Store.useDispatch();
  const runes = Store.useSelector(state => info.selectRunes(state), [info]);
  const skill = Data.skills[info.charClass][info.id];
  if (!skill) return null;

  let runePicker = null;

  if (info.multiple) {
    runePicker = (
      <React.Fragment>
        {Object.entries(info.runes).map(([rune, name]) => (
          <D3TooltipSkill key={rune} charClass={info.charClass} skill={info.id} rune={rune} onlyRune>
            <label className="d3p-skill-rune">
              <input type="checkbox" checked={!!runes[rune]} onChange={e => info.setRuneValue(dispatch, rune, e.target.checked)}/>
              <span className={`d3p-rune d3p-rune-${rune}`}/>{" "}
              {name}
            </label>
          </D3TooltipSkill>
        ))}
      </React.Fragment>
    );
  } else if (info.runelist.length > 1 || info.runelist === "*") {
    runePicker = <BuffRuneSelect info={info} value={runes} onChange={rune => info.setRuneValue(dispatch, rune)}/>;
  } else {
    runePicker = (
      <span className="d3p-skill-rune">
        <span className={`d3p-rune d3p-rune-${info.runelist}`}/>{" "}
        {info.runes[info.runelist]}
      </span>
    );
  }

  return (
    <D3TooltipSkill charClass={info.charClass} skill={info.id} rune={!info.multiple && runes}>
      <SkillHeader icon={<SkillIcon charClass={info.charClass} info={skill}/>} name={skill.name}>
        {runePicker}
      </SkillHeader>
    </D3TooltipSkill>
  );
}

function BuffHeaderPassive({ info, children }) {
  const passive = Data.passives[info.charClass][info.id];
  if (!passive) return null;
  return (
    <D3TooltipSkill charClass={info.charClass} skill={info.id}>
      <SkillHeader icon={<PassiveIcon charClass={info.charClass} info={passive}/>} name={passive.name}>
        {children}
      </SkillHeader>
    </D3TooltipSkill>
  );
}

function BuffHeaderAffix({ info, children }) {
  const value = Store.useSelector(state => info.params && info.params[0].selectValue(state).val, [info]);
  const item = Data.itemById[info.item];
  if (!item) return null;
  const data = {id: item.id, template: true};
  if (value != null) {
    data.custom = [value];
  }
  return (
    <D3TooltipItem data={info.tooltip || data}>
      <SkillHeader icon={<ItemIcon id={item.id}/>} name={item.name}>
        {children}
      </SkillHeader>
    </D3TooltipItem>
  );
}

function BuffHeaderGem({ info, children }) {
  const gem = Data.legendaryGems[info.id];
  if (!gem) return null;
  return (
    <D3TooltipGem id={info.id} level={25}>
      <SkillHeader icon={<ItemIcon id={info.id}/>} name={gem.name}>
        {children}
      </SkillHeader>
    </D3TooltipGem>
  );
}

const CustomIcon = ({ icon }) => (
  <span className="d3p-skill-icon d3p-skill-custom" style={{backgroundImage: `url(${getIconPath(icon)})`}}>
    <span className="d3p-skill-frame"/>
  </span>
);

function BuffHeaderFollower({ info, children }) {
  const skill = Data.followerSkills[info.id];
  if (!skill) return null;
  return (
    <D3TooltipFollowerSkill id={info.id}>
      <SkillHeader icon={<CustomIcon icon={skill.icon}/>} name={skill.name}>
        {children}
      </SkillHeader>
    </D3TooltipFollowerSkill>
  );
}

function BuffHeaderShrine({ info, children }) {
  const buff = Data.shrineBuffs[info.id];
  if (!buff) return null;
  return (
    <D3TooltipBuff id={info.id}>
      <SkillHeader icon={<CustomIcon icon={buff.icon}/>} name={buff.name}>
        {children}
      </SkillHeader>
    </D3TooltipBuff>
  );
}

const buffHeaders = {
  skill: BuffHeaderSkill,
  passive: BuffHeaderPassive,
  affix: BuffHeaderAffix,
  gem: BuffHeaderGem,
  follower: BuffHeaderFollower,
  shrine: BuffHeaderShrine,
};

export const BuffCard = React.memo(function BuffCard({ info }) {
  const BuffHeader = buffHeaders[info.type];
  if (!BuffHeader) return null;

  const extras = (
    <div className={info.type === "skill" ? "d3p-skill-extra" : "d3p-passive-extra"}>
      {!!info.setActive && <SkillActive info={info}/>}
      {!!info.boxnames && <SkillBoxes info={info}/>}
      {!!info.params && <SkillParams info={info}/>}
    </div>
  );

  return (
    <div className="d3p-SkillCard d3p-buff">
      <BuffHeader info={info}>
        {info.type !== "skill" && extras}
      </BuffHeader>
      {info.type === "skill" && extras}
    </div>
  );
});

function PartyBuffTabButton({ index }) {
  const charClass = Store.useSelector(state => Data.partybuffs[index].selectClass(state), [index]);
  if (charClass) {
    return <span className={`d3p-tab-icon d3p-class-icon d3p-class-${charClass}`}/>;
  } else {
    return <span className={`d3p-tab-icon d3p-tab-coop`}/>;
  }
}

const renderClass = charClass => (
  <span className="d3p-class-option">
    <span className={`d3p-class-${charClass} d3p-small d3p-class-icon`}/>
    {Data.classes[charClass].name}
  </span>
);

function PartyBuffTab({ index }) {
  const buffs = Data.partybuffs[index];
  const dispatch = Store.useDispatch();
  const charClass = Store.useSelector(state => buffs.selectClass(state), [buffs]);
  const setClass = React.useCallback(value => buffs.setClass(dispatch, value), [buffs, dispatch]);

  const getOptions = React.useCallback(() => Object.keys(Data.classes).filter(charClass => !Data.classes[charClass].follower), []);
  return (
    <React.Fragment>
      <Select options={getOptions}
              value={charClass}
              onChange={setClass}
              renderValue={renderClass}
              renderOption={renderClass}
              placeholder={_L`Select Class`}
              allowEmpty
              className="d3p-select-class"
      />

      {Object.entries(buffs.items).map(([id, info]) => {
        if (info.classes && !info.classes.includes(charClass)) return null;
        return <BuffCard key={id} info={info}/>
      })}

      {!!(charClass && buffs[charClass]) && Object.entries(buffs[charClass]).map(([id, info]) => <BuffCard key={id} info={info}/>)}
    </React.Fragment>
  );
}

// function FollowerBuffTab() {
//   const groups = {};
//   Object.entries(Data.followerbuffs).forEach(([id, info]) => {
//     const skill = Data.followerSkills[info.id];
//     if (!skill) return;
//     const group = Data.classes[skill.class].name;
//     if (!groups[group]) groups[group] = [];
//     groups[group].push(<BuffCard key={id} info={info}/>);
//   });
//   return Object.entries(groups).map(([name, buffs]) => (
//     <React.Fragment key={name}>
//       <h3>{name}</h3>
//       {buffs}
//     </React.Fragment>
//   ));
// }

function ShrineBuffTab() {
  const groups = {};
  Object.entries(Data.shrinebuffs).forEach(([id, info]) => {
    const group = info.category;
    if (!groups[group]) groups[group] = [];
    groups[group].push(<BuffCard key={id} info={info}/>);
  });
  return Object.entries(groups).map(([name, buffs]) => (
    <React.Fragment key={name}>
      <h3>{name}</h3>
      {buffs}
    </React.Fragment>
  ));
}

const tabList = [
  {id: "0", name: <PartyBuffTabButton index={0}/>},
  {id: "1", name: <PartyBuffTabButton index={1}/>},
  {id: "2", name: <PartyBuffTabButton index={2}/>},
  //{id: "follower", name: <span className={`d3p-tab-icon d3p-tab-follower`}/>},
  {id: "shrine", name: <span className={`d3p-tab-icon d3p-tab-shrine`}/>},
];

const tabPanes = {
  "0": <PartyBuffTab index={0}/>,
  "1": <PartyBuffTab index={1}/>,
  "2": <PartyBuffTab index={2}/>,
  //"follower": <FollowerBuffTab/>,
  "shrine": <ShrineBuffTab/>,
};

export default function BuffSection() {
  const [tab, setTab] = React.useState("0");
  return (
    <React.Fragment>
      <h3 className="d3p-skill-category">{_L`Group Buffs`}</h3>
      <p className="d3p-notice">{_L`The calculator does not check for correct buff stacking.`}</p>
      <div className="d3p-BuffTabs">
        <ul className="d3p-header">
          {tabList.map(({id, name}) => (
            <li key={id} className={classNames({"d3p-active": id === tab})} onClick={() => setTab(id)}>
              {name}
              {id === tab && <span className="d3p-active-border"/>}
            </li>
          ))}
        </ul>
        <div className="d3p-content">
          {tabPanes[tab]}
        </div>
      </div>
    </React.Fragment>
  );
}
